import _ from "lodash";
import { IShift, IShiftSession } from "../../../types/shifts/Shift";
import * as types from "../../actions/actionTypes";
import { IShiftSessionState, InitialState } from "../state";

const getPotentiallyUpdatedSession = (
  state: IShiftSessionState,
  session: IShiftSession,
) =>
  session._id in state.map
    ? { ...state.map[session._id], ...session }
    : session;

const getUpdatedStateWithNewShifts = (
  state: IShiftSessionState,
  shifts: IShift[],
) => {
  const mappedSessions: { [sessionId: string]: IShiftSession } = shifts
    .map((shft) =>
      _.keyBy(
        shft.sessions.map((sesh) => getPotentiallyUpdatedSession(state, sesh)),
        "_id",
      ),
    )
    .reduce((prev, nxt) => ({ ...prev, ...nxt }), {});
  const byShiftId: { [shiftId: string]: string[] } = shifts.reduce(
    (prev, shft) => ({
      ...prev,
      [shft._id]: shft.sessions.map((s) => s._id),
    }),
    {},
  );

  return {
    ...state,
    map: mappedSessions,
    byShiftId,
  };
};

const getNestedChange = (obj: object, keyParts: string[], newValue: any) => {
  if (keyParts.length === 0)
    throw Error("An internal error has occurred: Invalid number of key parts");
  const key = keyParts[0];
  if (_.isUndefined(key) || _.isNull(key)) return obj;
  if (keyParts.length === 1) return { ...obj, [key]: newValue };

  const nested = obj[key] || {}; // create parts of the object we don't have
  return {
    ...obj,
    [key]: getNestedChange(nested, keyParts.slice(1), newValue),
  };
};

const reducers: {
  [action: string]: (
    state: IShiftSessionState,
    data: any,
  ) => IShiftSessionState;
} = {
  [types.SHIFTS_GET_MANAGEABLE_FULFILLED]: (state, shifts) => ({
    ...getUpdatedStateWithNewShifts(state, shifts.shifts),
  }),
  [types.SHIFTS_GET_SUPERVISABLE_FULFILLED]: (state, shifts: IShift[]) => ({
    ...getUpdatedStateWithNewShifts(state, shifts),
  }),
  [types.SHIFTS_GET_SHIFT_STARTED]: (state: IShiftSessionState, key) => ({
    ...state,
    loading: {
      ...state.loading,
      [key]: { loading: true },
    },
  }),
  [types.SHIFTS_GET_SHIFT_REJECTED]: (
    state: IShiftSessionState,
    { shiftKey, message },
  ) => ({
    ...state,
    loading: {
      ...state.loading,
      [shiftKey]: { loading: false, error: message },
    },
  }),
  [types.SHIFTS_GET_SHIFT_FULFILLED]: (state, { processed }) => ({
    ...state,
    filled: {
      ...state.filled,
      [processed.key]: processed,
    },
    loading: {
      ...state.loading,
      [processed.key]: { loading: false },
    },
  }),
  [types.SHIFTS_ROSTER_SUPERVISOR_FULFILLED]: (state, { session }) =>
    getNestedChange(state, ["filled", session._id, "session"], session),
  [types.SHIFTS_REMOVE_SUPERVISOR_FULFILLED]: (state, { session }) =>
    getNestedChange(state, ["filled", session._id, "session"], session),
  [types.SHIFTS_ROSTER_ADMIN_FULFILLED]: (state, { session }) =>
    getNestedChange(state, ["filled", session._id, "session"], session),
  [types.SHIFTS_REMOVE_ADMIN_FULFILLED]: (state, { session }) =>
    getNestedChange(state, ["filled", session._id, "session"], session),
  [types.SHIFTS_PUBLISH_SESSION_FULFILLED]: (state, { shiftSession }) =>
    getNestedChange(
      state,
      ["filled", shiftSession?._id, "session"],
      shiftSession,
    ),
  [types.SHIFTS_ADD_SESSION_FULFILLED]: (
    state: IShiftSessionState,
    { shiftSession },
  ) => ({
    ...state,
    map: {
      ...state.map,
      [shiftSession._id]: shiftSession,
    },
    byShiftId: {
      ...state.byShiftId,
      [shiftSession.shift]: [
        ...(state.byShiftId[shiftSession.shift] || []),
        shiftSession._id,
      ],
    },
  }),
  [types.SHIFTS_REMOVE_SESSION_FULFILLED]: (
    state: IShiftSessionState,
    { shiftId, sessionId },
  ) => ({
    ...state,
    byShiftId: {
      ...state.byShiftId,
      [shiftId]: state.byShiftId[shiftId].filter((s) => s !== sessionId),
    },
  }),
  [types.SHIFTS_UPDATE_SESSION_FULFILLED]: (
    state: IShiftSessionState,
    { shiftSession },
  ) => ({
    ...state,
    map: {
      ...state.map,
      [shiftSession._id]: { ...state.map[shiftSession._id], ...shiftSession },
    },
  }),
  [types.APPOINTMENTS_GET_ACTIONABLE_FULFILLED]: (
    state: IShiftSessionState,
    { shiftSessions },
  ) => ({
    ...state,
    map: {
      ...state.map,
      ..._.keyBy(shiftSessions, "_id"),
    },
  }),
};

export default function shiftSessionsReducer(
  state: IShiftSessionState = InitialState.shiftSessions,
  action,
) {
  return action.type in reducers
    ? reducers[action.type](state, action.payload)
    : state;
}
